import type { MessageWithTitle } from '@ricoh/ria.client.core/types/component/data.type';
import type { i18n } from 'i18next';

import type { BaseErrorOptions, ErrorMessageTemplate } from './_base.type';

/** Base Error. */
export abstract class BaseError extends Error {
  /** ErrorMessage template. */
  errorMessageTemplate?: ErrorMessageTemplate;

  /**
   * Create Error instance.
   *
   * @param {BaseErrorOptions} options Options.
   */
  protected constructor({ message, errorMessageTemplate, cause }: BaseErrorOptions) {
    super(message, { cause });

    this.name = this.constructor.name;
    this.errorMessageTemplate = errorMessageTemplate;
  }

  /**
   * Create Error instance if cause is not Error.
   *
   * @abstract
   * @param {BaseErrorOptions} _options Options.
   *
   * @returns {BaseError} Error instance.
   */
  protected static create(_options: BaseErrorOptions): BaseError {
    throw new Error('Method not implemented.');
  }

  /**
   * Get Error message.
   *
   * @param {i18n['t']} t Translate function.
   *
   * @returns {MessageWithTitle} Error message.
   */
  errorMessage(t: i18n['t']): MessageWithTitle {
    return BaseError.resolveErrorMessageTemplate(t, this.errorMessageTemplate);
  }

  /**
   * To Payload.
   *
   * @returns {ErrorMessageTemplate | string} Payload.
   */
  toPayload(): ErrorMessageTemplate | string {
    return this.errorMessageTemplate ?? this.message;
  }

  /**
   * Resolve ErrorMessage Template.
   *
   * @param {i18n['t']} t Translate function.
   * @param {ErrorMessageTemplate} template ErrorMessage template.
   *
   * @returns {MessageWithTitle} Error message.
   */
  static resolveErrorMessageTemplate(t: i18n['t'], template?: ErrorMessageTemplate): MessageWithTitle {
    return {
      title: BaseError.resolveTemplateTitle(t, template),
      message: BaseError.resolveTemplateMessage(t, template),
    };
  }

  private static resolveTemplateTitle(t: i18n['t'], template?: ErrorMessageTemplate): string {
    const defaultTitle = 'error:app.title.default';
    if (!template?.title) {
      return t(defaultTitle);
    }

    if (typeof template.title === 'string') {
      return template.title;
    }

    return t([...template.title, defaultTitle]);
  }

  private static resolveTemplateMessage(t: i18n['t'], template?: ErrorMessageTemplate): string {
    const defaultMessage = 'error:app.message.default';
    if (!template?.message) {
      return t(defaultMessage);
    }

    if (typeof template.message === 'string') {
      return template.message;
    }

    return t([...template.message, defaultMessage], {
      ...template.values,
    });
  }
}
